/**
 * Site-scoped client-side routing for the multi-docket layout.
 *
 * The shared `Navigation` component derives the page slug from
 * `pathname.split("/")[1]`, which on this site is the docket selector, not the
 * page slug. Rather than change shared behavior, this site renders the shared
 * nav element but wires its own popstate/click routing here so the docket
 * segment is handled correctly:
 *
 *  - in-docket page links route client-side to `/<docket>/<page>`;
 *  - docket-boundary links (the root hub's links into each subsite, and links
 *    back to root) trigger a full page load so the SPA re-fetches the docket;
 *  - language links and in-page `#` anchors keep their default behavior, matching
 *    the shared Navigation guards.
 *
 * @module pathRouting
 */

import { outputDiv } from "commonAssets/js/constants";
import {
    resolvePageSlug,
    currentDocketSlug,
    crossesDocketBoundary,
} from "./resolveRoute";

/**
 * Wire popstate + click routing for the current page.
 *
 * @param {Object} ctx
 * @param {Array}  ctx.sitePages - The docket's pages (siteInfoLang.Pages).
 * @param {*}      ctx.importantDates - ImportantDates instance for getPage.
 * @param {Function} ctx.getPage - The site's page renderer.
 * @param {string[]} ctx.pathDockets - Configured docket slugs.
 * @param {Object} ctx.i18n - i18n instance (for language-link detection).
 * @returns {void}
 */
export function initPathRouting(ctx) {
    // The docket the currently-loaded CMS payload belongs to. A popstate that
    // lands on a different docket must reload so the right payload is fetched.
    const loadedDocket = currentDocketSlug(
        window.location.pathname,
        ctx.pathDockets
    );

    window.addEventListener("popstate", () => {
        const docket = currentDocketSlug(
            window.location.pathname,
            ctx.pathDockets
        );
        if (docket !== loadedDocket) {
            window.location.reload();
            return;
        }
        renderCurrent(ctx);
    });

    document.addEventListener("click", (e) => handleLinkClick(e, ctx));
}

/**
 * Render the page for the current URL within the loaded docket.
 * @param {Object} ctx - Routing context (see initPathRouting).
 * @returns {void}
 */
function renderCurrent(ctx) {
    outputDiv.innerHTML = ``;
    const slug = resolvePageSlug(window.location.pathname, ctx.pathDockets);
    setActiveNavLink(slug);
    ctx.getPage(ctx.sitePages, slug, ctx.importantDates);
}

/**
 * Handle a delegated click, routing in-docket links client-side.
 * @param {MouseEvent} e - The click event.
 * @param {Object} ctx - Routing context (see initPathRouting).
 * @returns {void}
 */
function handleLinkClick(e, ctx) {
    const link = e.target.closest("a");
    if (!link) {
        return;
    }
    // Collapse the mobile navbar on any link click, matching the shared
    // Navigation.pathChange behavior (which collapses on every click).
    closeNavbar();

    const raw = link.getAttribute("href");
    // In-page anchors (e.g. FAQ sections) and new-tab/external links keep
    // their default behavior.
    if (!raw || raw.startsWith("#")) {
        return;
    }
    if (link.getAttribute("target") === "_blank") {
        return;
    }
    if (!link.href.startsWith(window.location.origin)) {
        return;
    }

    const targetUrl = new URL(link.href);
    if (isLanguageLink(targetUrl, ctx.i18n)) {
        return;
    }

    // targetPath is a pure pathname for docket/slug resolution; targetHref adds
    // the query string and hash so client-side navigation does not drop them.
    const targetPath = resolveTargetPath(raw, targetUrl, ctx.pathDockets);
    const targetHref = targetPath + targetUrl.search + targetUrl.hash;

    // Crossing a docket boundary needs a full load so the correct CMS payload
    // is fetched; an SPA render would reuse the wrong docket's data.
    if (
        crossesDocketBoundary(
            window.location.pathname,
            targetPath,
            ctx.pathDockets
        )
    ) {
        e.preventDefault();
        window.location.assign(targetHref);
        return;
    }

    e.preventDefault();
    window.history.pushState({}, "", targetHref);
    renderCurrent(ctx);
}

/**
 * Resolve a clicked link's raw href to an absolute target path.
 *
 * Bare page-name hrefs (the shared Navigation renders `href="faqs"`) are
 * resolved within the current docket; absolute-path hrefs are taken as-is.
 *
 * @param {string} raw - The link's raw `href` attribute.
 * @param {URL} targetUrl - The resolved link URL.
 * @param {string[]} pathDockets - Configured docket slugs.
 * @returns {string} The absolute target pathname.
 */
function resolveTargetPath(raw, targetUrl, pathDockets) {
    // Absolute path ("/...") or full URL ("https://..."): trust the resolved
    // pathname. Only bare page-name hrefs (the shared nav renders href="faqs")
    // are resolved within the current docket.
    if (raw.startsWith("/") || /^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
        return targetUrl.pathname;
    }
    // Strip any query/hash so the result is a pure pathname; the caller
    // re-attaches search and hash from the resolved URL for navigation.
    const rawPath = raw.split(/[?#]/)[0];
    const docket = currentDocketSlug(window.location.pathname, pathDockets);
    return docket ? `/${docket}/${rawPath}` : `/${rawPath}`;
}

/**
 * Detect language-switch links so they keep their default behavior, matching
 * the shared Navigation component's guard.
 * @param {URL} targetUrl - The resolved link URL.
 * @param {Object} i18n - i18n instance exposing a `languages` array.
 * @returns {boolean} True when the link is a language switch.
 */
function isLanguageLink(targetUrl, i18n) {
    const langs = (i18n && i18n.languages) || [];
    const fromQuery = targetUrl.searchParams.get("language");
    const fromPath = targetUrl.pathname.split("/").filter(Boolean)[0];
    return (
        (fromQuery && langs.includes(fromQuery)) ||
        (fromPath && langs.includes(fromPath))
    );
}

/**
 * Toggle the active state on nav links to match the rendered page slug.
 * Mirrors the shared Navigation `isPageActive` behavior.
 * @param {string} slug - The active page slug ("" denotes home).
 * @returns {void}
 */
function setActiveNavLink(slug) {
    document.querySelectorAll(".nav-link").forEach((link) => {
        const name = link.getAttribute("href");
        const active = name === slug || (slug === "" && name === "home");
        link.classList.toggle("active", active);
        if (active) {
            link.setAttribute("aria-current", "page");
        } else {
            link.removeAttribute("aria-current");
        }
    });
}

/**
 * Collapse the mobile navbar after a navigation, matching shared behavior.
 * @returns {void}
 */
function closeNavbar() {
    const navbar = document.querySelector("#app-navbar");
    if (navbar) {
        navbar.classList.remove("show");
    }
}
